<!-- Only render when the active project has consumption to show -->
@if (visible()) {
<!-- Trigger button: compact consumption indicator in the header -->
<div
    class="resource-meter-trigger"
    cdkOverlayOrigin
    #trigger="cdkOverlayOrigin"
    [class.placeholder]="placeholder"
    (click)="placeholder ? null : togglePanel()"
    [matTooltip]="placeholder
        ? 'Placeholder. Usage costs come from the Payment API, which this build cannot reach — the real figure appears in production.'
        : (mode() === 'payg'
            ? 'Estimated usage costs this month – click for details'
            : 'Resource usage – click for details')"
>
    @if (placeholder) {
    <!-- Same shape, no data: an empty track and a dash. -->
    <mat-icon class="meter-icon icon-size-4" [svgIcon]="'heroicons_outline:chart-bar'"></mat-icon>
    <div class="meter-bar-track"></div>
    <span class="meter-percent">—</span>
    } @else if (mode() === 'payg') {
    <!-- Pay-as-you-go: month-to-date estimated cost chip -->
    <mat-icon class="meter-icon icon-size-4" [svgIcon]="'heroicons_outline:banknotes'"></mat-icon>
    <span class="meter-cost-chip">≈ {{ formatCost(totalCostCents() ?? 0) }}</span>
    } @else {
    <!-- Limited plans: combined worst-case consumption bar (fills left to right) -->
    <mat-icon class="meter-icon icon-size-4" [svgIcon]="'heroicons_outline:chart-bar'"></mat-icon>

    <div class="meter-bar-track">
        <div
            class="meter-bar-fill"
            [class.healthy]="getStatusColor(worstPercent()) === 'healthy'"
            [class.warning]="getStatusColor(worstPercent()) === 'warning'"
            [class.critical]="getStatusColor(worstPercent()) === 'critical'"
            [style.width.%]="(worstPercent() ?? 0) * 100"
        ></div>
    </div>

    <!-- Consumed percentage label -->
    <span class="meter-percent" [class]="getStatusColor(worstPercent())">
        {{ ((worstPercent() ?? 0) * 100) | number:'1.0-0' }}%
    </span>
    }
</div>

<!-- Detail overlay panel -->
<ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="trigger"
    [cdkConnectedOverlayOpen]="panelOpen()"
    [cdkConnectedOverlayHasBackdrop]="true"
    [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
    (backdropClick)="closePanel()"
    [cdkConnectedOverlayPositions]="[
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 }
    ]"
>
    <div class="resource-meter-panel">
        <!-- Panel header -->
        <div class="panel-header">
            <span class="panel-title">{{ mode() === 'payg' ? 'Usage & Costs' : 'Resource Usage' }}</span>
            @if (activeApp(); as app) {
            <span class="panel-app-name">{{ app.name }}</span>
            }
        </div>

        <!-- Meter rows grouped by product -->
        <div class="panel-meters">
            @for (group of meterGroups(); track group.slug) {
            <div class="panel-product-group">
                <div class="panel-product-caption">{{ group.name }}</div>
                @for (meter of group.meters; track meter.kpiId) {
                <div class="meter-row">
                    <div class="meter-row-header">
                        <mat-icon class="icon-size-4 meter-row-icon" [svgIcon]="meter.icon"></mat-icon>
                        <span class="meter-row-label">{{ displayLabel(meter) }}</span>
                        <span
                            class="meter-row-values"
                            [matTooltip]="meter.costCents === 0 && meter.unitPriceCents === null
                                ? 'No pricing available yet'
                                : ''"
                        >
                            @if (meter.included !== null) {
                            {{ formatAmount(meter.consumed) }} / {{ formatAmount(meter.included) }} {{ meter.unit }}
                            } @else if (meter.costCents !== null) {
                            {{ formatAmount(meter.consumed) }} {{ meter.unit }} · {{ formatCost(meter.costCents, meter.currency) }}
                            } @else {
                            {{ formatAmount(meter.consumed) }} {{ meter.unit }}
                            }
                        </span>
                    </div>
                    @if (meter.consumedPercent !== null) {
                    <!-- Consumed fraction, filling left to right -->
                    <div class="meter-row-bar-track">
                        <div
                            class="meter-row-bar-fill"
                            [class.healthy]="getStatusColor(meter.consumedPercent) === 'healthy'"
                            [class.warning]="getStatusColor(meter.consumedPercent) === 'warning'"
                            [class.critical]="getStatusColor(meter.consumedPercent) === 'critical'"
                            [style.width.%]="meter.consumedPercent * 100"
                        ></div>
                    </div>
                    }
                    @if (meter.rechargeAt) {
                    <div class="meter-row-note">
                        Recharges {{ meter.rechargeAt | date:'mediumDate' }}
                    </div>
                    }
                    @if (meter.infoNote) {
                    <div class="meter-row-note">{{ meter.infoNote }}</div>
                    }
                </div>
                }
            </div>
            }

            @if (mode() === 'payg') {
            <div class="panel-total-row">
                <span>Estimated total</span>
                <span>≈ {{ formatCost(totalCostCents() ?? 0) }}</span>
            </div>
            <div class="panel-estimate-note">
                Live estimate — excludes fixed fees and discounts.
            </div>
            }
        </div>

        <!-- Panel footer with links to usage details and plan settings -->
        <div class="panel-footer">
            <button class="panel-settings-link" (click)="goToConsumption()">
                <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:chart-bar'"></mat-icon>
                <span>Usage & Costs</span>
            </button>
            <button class="panel-settings-link" (click)="goToSettings()">
                <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:cog-6-tooth'"></mat-icon>
                <span>Manage Plan</span>
            </button>
        </div>
    </div>
</ng-template>
}
